import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { toast } from "sonner";
import { 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  ServerCrash, 
  Filter, 
  Search,
  Plus,
  MoreHorizontal 
} from "lucide-react";
import { 
  fetchFeatureRequests, 
  createFeatureRequest, 
  updateFeatureRequest, 
  getFeatureRequestStatusCounts,
  useCreateFeatureRequest
} from "@/lib/services/feature-request-service";
import { FeatureRequest, StatusCounts } from "@/lib/types/feature-management";
import { useAuth } from "@/lib/context/auth-context";
import { format } from "date-fns";

export default function FeatureRequests() {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [isNewRequestOpen, setIsNewRequestOpen] = useState(false);
  const [isViewDetailsOpen, setIsViewDetailsOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<FeatureRequest | null>(null);
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    priority: "high" | "medium" | "low";
    department: string;
  }>({
    title: "",
    description: "",
    priority: "medium",
    department: "User Experience"
  });
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const { submit } = useCreateFeatureRequest();

  const { 
    data: requests = [], 
    isLoading: isLoadingRequests,
    error: requestsError
  } = useQuery({
    queryKey: ['feature-requests'],
    queryFn: fetchFeatureRequests
  });

  const { 
    data: statusCounts = { pending: 0, inProgress: 0, completed: 0, highPriority: 0 },
    isLoading: isLoadingCounts
  } = useQuery({
    queryKey: ['feature-request-counts'],
    queryFn: getFeatureRequestStatusCounts
  });

  const createRequestMutation = useMutation({
    mutationFn: submit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-requests'] });
      queryClient.invalidateQueries({ queryKey: ['feature-request-counts'] });
      setIsNewRequestOpen(false);
      resetForm();
    }
  });

  const updateRequestMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<FeatureRequest> }) => {
      console.log(`Updating request with ID: ${id}`, updates);
      return updateFeatureRequest(id, updates);
    },
    onSuccess: (data) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ['feature-requests'] });
        queryClient.invalidateQueries({ queryKey: ['feature-request-counts'] });
        toast.success('Status updated successfully');
      } else {
        toast.error('Failed to update status');
      }
      setIsViewDetailsOpen(false);
    },
    onError: (error) => {
      console.error('Update mutation error:', error);
      toast.error('Failed to update feature request status');
    }
  });

  useEffect(() => {
    if (requestsError) {
      toast.error("Failed to load feature requests");
      console.error(requestsError);
    }
  }, [requestsError]);

  const filteredRequests = requests.filter(request => {
    const matchesSearch = request.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          request.id.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || request.status === statusFilter;
    const matchesPriority = priorityFilter === "all" || request.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      priority: "medium",
      department: "User Experience"
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description) {
      toast.error("Please fill out all required fields");
      return;
    }

    createRequestMutation.mutate(formData);
  };

  const handleUpdateStatus = (requestId: string, newStatus: string) => {
    if (!requestId) {
      toast.error('Invalid request ID');
      return;
    }
    
    console.log(`Handling status update for ID: ${requestId} to ${newStatus}`);
    
    updateRequestMutation.mutate({
      id: requestId,
      updates: { 
        status: newStatus as FeatureRequest['status'],
        updated_at: new Date().toISOString()
      }
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary">Pending Review</Badge>;
      case "approved":
        return <Badge variant="accent">Approved</Badge>;
      case "in-progress":
        return <Badge variant="primary-subtle">In Progress</Badge>;
      case "completed":
        return <Badge variant="success" className="bg-green-500">Completed</Badge>;
      case "rejected":
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "high":
        return <Badge variant="destructive" className="bg-red-500">High</Badge>;
      case "medium":
        return <Badge variant="accent">Medium</Badge>;
      case "low":
        return <Badge variant="secondary">Low</Badge>;
      default:
        return <Badge>{priority}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const isStaffOrAdmin = profile?.role === 'staff' || profile?.role === 'admin' || profile?.role === 'superadmin' || profile?.role === 'tech';

  return (
    <div className="container mx-auto pt-6 px-6 space-y-6 max-w-7xl animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-title font-bold tracking-tight text-primary">Feature Requests</h1>
          <p className="text-muted-foreground font-subtitle">Manage and prioritize user feature requests</p>
        </div>
        <Button className="bg-accent hover:bg-accent/90" onClick={() => setIsNewRequestOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Request
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white shadow-sm border-l-4 border-l-primary">
          <CardContent className="p-6 flex flex-col items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-primary mb-2" />
            <CardTitle className="text-xl mb-1 font-title">Pending</CardTitle>
            <p className="text-3xl font-bold">{isLoadingCounts ? '...' : statusCounts.pending}</p>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm border-l-4 border-l-accent">
          <CardContent className="p-6 flex flex-col items-center justify-center">
            <Clock className="h-8 w-8 text-accent mb-2" />
            <CardTitle className="text-xl mb-1 font-title">In Progress</CardTitle>
            <p className="text-3xl font-bold">{isLoadingCounts ? '...' : statusCounts.inProgress}</p>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm border-l-4 border-l-green-500">
          <CardContent className="p-6 flex flex-col items-center justify-center">
            <CheckCircle className="h-8 w-8 text-green-500 mb-2" />
            <CardTitle className="text-xl mb-1 font-title">Completed</CardTitle>
            <p className="text-3xl font-bold">{isLoadingCounts ? '...' : statusCounts.completed}</p>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm border-l-4 border-l-red-500">
          <CardContent className="p-6 flex flex-col items-center justify-center">
            <ServerCrash className="h-8 w-8 text-red-500 mb-2" />
            <CardTitle className="text-xl mb-1 font-title">High Priority</CardTitle>
            <p className="text-3xl font-bold">{isLoadingCounts ? '...' : statusCounts.highPriority}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-lg border border-accent-light/30 bg-white shadow-sm">
        <CardHeader className="bg-accent-subtle/20 border-b border-accent-light/30 p-4">
          <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
            <CardTitle className="text-xl font-bold font-title text-primary">All Feature Requests</CardTitle>
            <div className="flex flex-col space-y-2 md:flex-row md:space-x-2 md:space-y-0">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search requests..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 w-full md:w-[200px] font-subtitle"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-[180px] font-subtitle">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-full md:w-[180px] font-subtitle">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-accent-subtle/10">
              <TableRow className="border-b border-accent-light/30">
                <TableHead className="font-section text-primary">ID</TableHead>
                <TableHead className="font-section text-primary">Request</TableHead>
                <TableHead className="font-section text-primary">Department</TableHead>
                <TableHead className="font-section text-primary">Status</TableHead>
                <TableHead className="font-section text-primary">Priority</TableHead>
                <TableHead className="font-section text-primary">Assigned To</TableHead>
                <TableHead className="font-section text-primary">Date</TableHead>
                <TableHead className="text-right font-section text-primary">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingRequests ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                    <div className="mt-2 text-muted-foreground">Loading requests...</div>
                  </TableCell>
                </TableRow>
              ) : filteredRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="text-muted-foreground">No feature requests found</div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRequests.map((request) => (
                  <TableRow key={request.id} className="hover:bg-accent-subtle/20 border-b border-accent-light/20">
                    <TableCell className="font-mono text-sm">{request.id.substring(0, 8)}</TableCell>
                    <TableCell className="font-body font-medium">{request.title}</TableCell>
                    <TableCell className="font-body">{request.department}</TableCell>
                    <TableCell>{getStatusBadge(request.status)}</TableCell>
                    <TableCell>{getPriorityBadge(request.priority)}</TableCell>
                    <TableCell className="font-body">{request.assigned_to}</TableCell>
                    <TableCell className="font-body text-sm">{formatDate(request.date_submitted)}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-primary hover:bg-accent-subtle/30"
                        onClick={() => {
                          setSelectedRequest(request);
                          setIsViewDetailsOpen(true);
                        }}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isNewRequestOpen} onOpenChange={setIsNewRequestOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-title text-xl">Submit Feature Request</DialogTitle>
            <DialogDescription>
              Provide details about the feature you'd like to request.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="font-section">Title</Label>
              <Input 
                id="title" 
                placeholder="Brief descriptive title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description" className="font-section">Description</Label>
              <Textarea 
                id="description" 
                placeholder="Detailed description of the feature"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="min-h-[100px]"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="priority" className="font-section">Priority</Label>
              <Select 
                value={formData.priority}
                onValueChange={(value) => setFormData({...formData, priority: value as 'high' | 'medium' | 'low'})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="department" className="font-section">Department</Label>
              <Select 
                value={formData.department}
                onValueChange={(value) => setFormData({...formData, department: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="User Experience">User Experience</SelectItem>
                  <SelectItem value="Certification">Certification</SelectItem>
                  <SelectItem value="Administration">Administration</SelectItem>
                  <SelectItem value="Data Management">Data Management</SelectItem>
                  <SelectItem value="Analytics">Analytics</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter className="mt-6">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  resetForm();
                  setIsNewRequestOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-primary"
                disabled={createRequestMutation.isPending}
              >
                {createRequestMutation.isPending ? 'Submitting...' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isViewDetailsOpen} onOpenChange={setIsViewDetailsOpen}>
        <DialogContent className="sm:max-w-md">
          {selectedRequest && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xs">{selectedRequest.id.substring(0, 8)}</span>
                  {getStatusBadge(selectedRequest.status)}
                </div>
                <DialogTitle className="font-title text-xl">{selectedRequest.title}</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 my-2">
                <div>
                  <h4 className="text-sm font-section text-muted-foreground mb-1">Description</h4>
                  <p className="font-body">{selectedRequest.description}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-section text-muted-foreground mb-1">Submitted By</h4>
                    <p className="font-body">{selectedRequest.submitted_by_name}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-section text-muted-foreground mb-1">Date Submitted</h4>
                    <p className="font-body">{formatDate(selectedRequest.date_submitted)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-section text-muted-foreground mb-1">Department</h4>
                    <p className="font-body">{selectedRequest.department}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-section text-muted-foreground mb-1">Priority</h4>
                    <div>{getPriorityBadge(selectedRequest.priority)}</div>
                  </div>
                  <div>
                    <h4 className="text-sm font-section text-muted-foreground mb-1">Assigned To</h4>
                    <p className="font-body">{selectedRequest.assigned_to}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-section text-muted-foreground mb-1">Status</h4>
                    <div>{getStatusBadge(selectedRequest.status)}</div>
                  </div>
                </div>
              </div>
              
              {isStaffOrAdmin && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-section text-muted-foreground mb-2">Update Status</h4>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      size="sm" 
                      variant={selectedRequest.status === 'pending' ? 'default' : 'outline'}
                      onClick={() => handleUpdateStatus(selectedRequest.id, 'pending')}
                      disabled={updateRequestMutation.isPending}
                    >
                      Pending
                    </Button>
                    <Button 
                      size="sm" 
                      variant={selectedRequest.status === 'approved' ? 'default' : 'outline'}
                      className={selectedRequest.status === 'approved' ? 'bg-accent' : ''}
                      onClick={() => handleUpdateStatus(selectedRequest.id, 'approved')}
                      disabled={updateRequestMutation.isPending}
                    >
                      Approved
                    </Button>
                    <Button 
                      size="sm" 
                      variant={selectedRequest.status === 'in-progress' ? 'default' : 'outline'}
                      className={selectedRequest.status === 'in-progress' ? 'bg-primary' : ''}
                      onClick={() => handleUpdateStatus(selectedRequest.id, 'in-progress')}
                      disabled={updateRequestMutation.isPending}
                    >
                      In Progress
                    </Button>
                    <Button 
                      size="sm" 
                      variant={selectedRequest.status === 'completed' ? 'default' : 'outline'}
                      className={selectedRequest.status === 'completed' ? 'bg-green-500' : ''}
                      onClick={() => handleUpdateStatus(selectedRequest.id, 'completed')}
                      disabled={updateRequestMutation.isPending}
                    >
                      Completed
                    </Button>
                    <Button 
                      size="sm" 
                      variant={selectedRequest.status === 'rejected' ? 'default' : 'outline'}
                      className={selectedRequest.status === 'rejected' ? 'bg-destructive' : ''}
                      onClick={() => handleUpdateStatus(selectedRequest.id, 'rejected')}
                      disabled={updateRequestMutation.isPending}
                    >
                      Rejected
                    </Button>
                  </div>
                </div>
              )}
              
              <DialogFooter className="mt-6">
                <Button 
                  onClick={() => setIsViewDetailsOpen(false)}
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
